import { makeAutoObservable, runInAction } from 'mobx';

import { cmApi } from '@src/api';
import { preloaderStore } from '@stores/preloaderStore';
import type { CmApi, ControlVersionDto } from '@src/api';

import { controlVersionsStore } from '../controlVersionsStore';

class ControlVersionEditStore {
  controlVersion: ControlVersionDto | null;

  isNew = false;

  editMode = false;

  isLoading = false;

  api: CmApi;

  hiddenEdit = false;

  hiddenDelete = false;

  hiddenClean = false;

  hiddenSave = false;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    this.controlVersion = null;

    makeAutoObservable(this);
  }

  get isEditMode() {
    return this.editMode || this.isNew;
  }

  setNew = (isNew = true) => {
    this.isNew = isNew;
    this.controlVersion = null;
  };

  setEditMode = (editMode = true) => {
    this.editMode = editMode;
  };

  save = async (controlVersion: ControlVersionDto) => {
    preloaderStore.preloader(true);
    const { data } = await (this.isNew
      ? this.api.controlVersions.addControlVersion(controlVersion)
      : this.api.controlVersions.updateControlVersion(controlVersion));

    if (data) {
      controlVersionsStore.setIsChanged(true);

      if (this.isNew) {
        preloaderStore.preloader(false);
        return { ctrlVersCode: data.ctrlVersCode };
      }
      runInAction(() => {
        this.controlVersion = {
          ...data,
          steps: data.steps?.map((step) => {
            const stepSetting = data.stepSettings?.find((setting) => setting.stepNum === step.stepNum);
            return {
              ...step,
              ...stepSetting,
              cond: !!stepSetting?.premise,
              isFnsControl: !!step?.codeFns,
              id: step.stepNum,
            };
          }),
        };
        this.setEditMode(false);
      });
    }
    preloaderStore.preloader(false);
    return undefined;
  };

  delete = async () => {
    if (this.controlVersion) {
      await this.api.controlVersions.deleteControlVersionByCtrlVersCode(this.controlVersion.ctrlVersCode ?? '');
      controlVersionsStore.setIsChanged(true);
    }
  };

  fetch = async (ctrlVersCode?: string): Promise<void> => {
    if (!ctrlVersCode) {
      return;
    }
    preloaderStore.preloader(true);
    runInAction(() => {
      this.isLoading = true;
      this.setEditMode(false);
      this.setNew(false);
    });

    const { data } = await this.api.controlVersions.getControlVersionByCtrlVersCode(ctrlVersCode);

    if (data) {
      runInAction(() => {
        this.controlVersion = {
          ...data,
          steps: data.steps?.map((step) => {
            const stepSetting = data.stepSettings?.find((setting) => setting.stepNum === step.stepNum);
            return {
              ...step,
              ...stepSetting,
              cond: !!stepSetting?.premise,
              isFnsControl: !!step?.codeFns,
              id: step.stepNum,
            };
          }),
        };
        this.isLoading = false;
      });
    }
    preloaderStore.preloader(false);
  };

  setHiddenEdit = (hiddenEdit = true): void => {
    this.hiddenEdit = hiddenEdit;
  };

  setHiddenDelete = (hiddenDelete = true): void => {
    this.hiddenDelete = hiddenDelete;
  };

  setHiddenClean = (hiddenClean = true): void => {
    this.hiddenClean = hiddenClean;
  };

  setHiddenSave = (hiddenSave = true): void => {
    this.hiddenSave = hiddenSave;
  };
}

export const controlVersionEditStore = new ControlVersionEditStore({ api: cmApi });
